import { useCallback, useContext, useMemo, useRef } from 'react'
import { RoomContext, RoomContextType } from '../../RoomContext'
import useRemoteDesktopControlPermissionActions from '../../services/useRemoteDesktopControlPermissionActions'
import useRoomActions from '../../services/useRoomActions'
import { UserContext, UserContextType } from '../../UserContext'
import ConfirmationDialogContents from '../ConfirmationDialogContents'
import RecordKeyView from '../RecordKeyView'
import RemoteDesktopControlAskPermissionPopup from './RemoteDesktopControlAskPermissionPopup'
import * as Constants from '../../services/Constants'
import useMiscRoomActions from '../../services/useMiscRoomActions'
import RoomWaitlistDialog from '../../dialogs/RoomWaitlistDialog'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import RemoteDesktopControlPermisionDecisionPopup from './RemoteDesktopControlPermissionDecisionPopup'
import AudioEngineIncompatiblePeerWarningDialog from '../../dialogs/AudioEngineIncompatiblePeerWarningDialog'
import useRoomViews from '../../services/useRoomViews'
import useAnalytics from '../../services/useAnalytics'
import { AnalyticsEvents } from '../../services/AnalyticsService'
import { InviteCollaborators } from '../SessionPage/internal/gallery/modes/Invite'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'
import RemoteAudioSettingsDialogController from '../RemoteAudioSettings/RemoteAudioSettingsDialogController'

export default function CollaboratePageDialogs() {
    const { state } = useContext(UserContext) as UserContextType
    const { state: roomState, dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { t } = useTranslation()

    const { showWaitingRoomPopup: showInvitePopup } = useRoomViews()

    const BeingRemoteRecordedPopup_anchorRef = useRef(null)

    const { logUserActivityWithContext } = useAnalytics()

    const { handleWaitlistUser, leaveRoom, closeRecordTalkbackKeyDialog, removePeer } = useRoomActions()

    const { setShowWaitingRoomPopup, closeRoomWaitlistDialog } = useMiscRoomActions()

    const { ask: remoteDesktopControlPermissionAsk, allowOrDeny: remoteDesktopControlPermissionAllowOrDeny } =
        useRemoteDesktopControlPermissionActions()

    const closeIsLeaveConfirmationDialogOpen = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: { isLeaveConfirmationDialogOpen: null },
        })
    }, [roomDispatch])

    const exitRoomConfirmationMessage = useMemo(() => {
        if (roomState.isLeaveConfirmationDialogOpen) {
            return mkExitRoomConfirmationMessage(
                t,
                roomState.isLeaveConfirmationDialogOpen.endSession,
                state.profile?.policy?.roomAutoDeleteDurationHr || 0
            )
        } else {
            return ''
        }
    }, [roomState.isLeaveConfirmationDialogOpen, state.profile])

    return (
        <>
            {/* InviteCollaborators Popup */}
            {showInvitePopup && roomState.roomUrn && !roomState.isLeaving && (
                <CrossPlatformDialog
                    frameName={`Dialog:InviteCollaborators`}
                    classNameNative="min-w-[480px]"
                    classNameBrowser="md:w-[480px]"
                    onDefaultClose={() => setShowWaitingRoomPopup(false)}
                >
                    <div className="flex flex-col gap-6 p-4 pt-0 w-full h-full">
                        <div className="t-title-dialog">{t('Invite Users to Session')}</div>
                        <InviteCollaborators onSent={() => setShowWaitingRoomPopup(false)} />
                    </div>
                </CrossPlatformDialog>
            )}

            {/* Waitlist dialog that shows up when a user requests to join the room */}
            {roomState.isRoomWaitlistDialogOpen && roomState.roomWaitlist.length > 0 && (
                <CrossPlatformDialog
                    classNameBrowser="flex flex-col"
                    frameName={`Dialog:roomWaitlistDialog`}
                    alwaysOnTop
                    noDefaultScroll
                    onDefaultClose={closeRoomWaitlistDialog}
                >
                    <RoomWaitlistDialog
                        roomWaitlist={roomState.roomWaitlist}
                        onWaitlistedUserHandledResult={handleWaitlistUser}
                    />
                </CrossPlatformDialog>
            )}

            {roomState.isLeaveConfirmationDialogOpen && (
                <CrossPlatformDialog
                    frameName={'Dialog:LeaveConfirmationDialog'}
                    alwaysOnTop
                    onDefaultClose={closeIsLeaveConfirmationDialogOpen}
                >
                    <ConfirmationDialogContents
                        message={exitRoomConfirmationMessage}
                        onPositive={() => {
                            console.log(`CollaboratePage: Dialog: at onPositive`)
                            closeIsLeaveConfirmationDialogOpen()
                            console.log(`CollaboratePage: Dialog: onPositive: calling leaveSessionHandler()`)
                            leaveRoom({ endSession: roomState.isLeaveConfirmationDialogOpen?.endSession || false })
                        }}
                        onNegative={closeIsLeaveConfirmationDialogOpen}
                        positiveButtonText={
                            (roomState.isLeaveConfirmationDialogOpen?.endSession ?? false)
                                ? t('End Session')
                                : t('Leave Session')
                        }
                        negativeButtonText={t('Cancel') || ''}
                    />
                </CrossPlatformDialog>
            )}

            {roomState.isRemoteDesktopControlQueryPromptOpen && (
                <RemoteDesktopControlAskPermissionPopup
                    name={roomState.isRemoteDesktopControlQueryPromptOpen.username}
                    onClose={(askForPermission: boolean) => {
                        if (askForPermission && roomState.isRemoteDesktopControlQueryPromptOpen) {
                            // RemoteDesktopControlPermissions.ask({ peerSessionId: peer.sessionId })
                            remoteDesktopControlPermissionAsk({
                                peerSessionId: roomState.isRemoteDesktopControlQueryPromptOpen.sessionId,
                            })
                            logUserActivityWithContext(AnalyticsEvents.DESKTOP_CONTROL_PERMISSION_REQUESTED, {
                                peerSessionId: roomState.isRemoteDesktopControlQueryPromptOpen.sessionId,
                                screenOwner: roomState.isRemoteDesktopControlQueryPromptOpen.username,
                                requesterUsername: state.profile?.username || '',
                            })
                        }
                        roomDispatch({
                            type: 'update_room_context',
                            value: { isRemoteDesktopControlQueryPromptOpen: null },
                        })
                    }}
                />
            )}

            {roomState.isRemoteDesktopControlRequestPromptOpen && (
                <RemoteDesktopControlPermisionDecisionPopup
                    fromUsername={roomState.isRemoteDesktopControlRequestPromptOpen.fromUsername}
                    onResult={(isAllowed: boolean) => {
                        const data = roomState.isRemoteDesktopControlRequestPromptOpen
                        if (data) {
                            remoteDesktopControlPermissionAllowOrDeny({
                                sessionId: data.fromSessionId,
                                username: data.fromUsername,
                                reqId: data.reqId,
                                status: isAllowed ? 'allowed' : 'denied',
                            })
                            if (isAllowed) {
                                logUserActivityWithContext(AnalyticsEvents.DESKTOP_CONTROL_PERMISSION_GRANTED, {
                                    screenOwner: state.profile?.username || '',
                                    requesterUsername: data.fromUsername,
                                })
                            } else {
                                logUserActivityWithContext(AnalyticsEvents.DESKTOP_CONTROL_PERMISSION_DENIED, {
                                    screenOwner: state.profile?.username || '',
                                    requesterUsername: data.fromUsername,
                                })
                            }
                        }
                    }}
                    onClose={() => {
                        roomDispatch({
                            type: 'update_room_context',
                            value: { isRemoteDesktopControlRequestPromptOpen: null },
                        })
                    }}
                />
            )}

            {roomState.isRecordTalkbackKeyDialogOpen && (
                <CrossPlatformDialog frameName="Dialog:RecordTalkbackKey" onDefaultClose={closeRecordTalkbackKeyDialog}>
                    <RecordKeyView onClose={closeRecordTalkbackKeyDialog} />
                </CrossPlatformDialog>
            )}

            <div
                id="BeingRemoteRecordedPopup_anchor"
                ref={BeingRemoteRecordedPopup_anchorRef}
                style={{ position: 'absolute', top: 40 + Constants.title_bar_height_px, left: '50vw' }}
            ></div>

            {/* {roomState.remoteRecordingStatus.isBeingRecorded && (
                <BeingRemoteRecordedPopup
                    show={true}
                    remoteRecordingStatus={roomState.remoteRecordingStatus}
                    anchorRef={BeingRemoteRecordedPopup_anchorRef}
                    onClose={() => {}}
                />
            )} */}

            {roomState.isRemoveUserConfirmationDialogOpen && (
                <CrossPlatformDialog
                    frameName="Dialog:RemoveUserConfirmationDialog"
                    onDefaultClose={() => removePeer(null)}
                >
                    <ConfirmationDialogContents
                        message={t(`Are you sure you want to remove user {{username}} from this session?`, {
                            username: roomState.isRemoveUserConfirmationDialogOpen.username,
                        })}
                        onPositive={() => {
                            removePeer(roomState.isRemoveUserConfirmationDialogOpen?.sessionId || null)
                        }}
                        onNegative={() => removePeer(null)}
                        positiveButtonText={t('Remove') || ''}
                        negativeButtonText={t('Cancel') || ''}
                    />
                </CrossPlatformDialog>
            )}

            {roomState.audioEngineIncompatiblePeerWarningDialog.isOpen && (
                <AudioEngineIncompatiblePeerWarningDialog
                    data={roomState.audioEngineIncompatiblePeerWarningDialog}
                    onClose={() => {}}
                />
            )}

            {roomState.isCannotPlayAudioDialogOpen && (
                <CrossPlatformDialog
                    frameName="Dialog:CannotPlayAudioDialog"
                    onDefaultClose={() => {
                        roomDispatch({
                            type: 'update_room_context',
                            value: { isCannotPlayAudioDialogOpen: false },
                        })
                    }}
                >
                    <ConfirmationDialogContents
                        message={t(`Enable audio playback`)}
                        onPositive={() => {
                            if (roomState.sfuRoom) {
                                roomState.sfuRoom.startAudio()
                            }

                            roomDispatch({
                                type: 'update_room_context',
                                value: { isCannotPlayAudioDialogOpen: false },
                            })
                        }}
                        positiveButtonText={t('OK') || ''}
                    />
                </CrossPlatformDialog>
            )}

            <RemoteAudioSettingsDialogController />
        </>
    )
}

const mkExitRoomConfirmationMessage = (
    t: TFunction<'translation', undefined>,
    endSession: boolean,
    roomAutoDeleteDurationHr: number
) => {
    if (endSession) {
        if (roomAutoDeleteDurationHr) {
            return t(
                'Are you sure you want to end this session?\nAll of the collaborators will be removed from the room, and the room will be deleted after {{roomAutoDeleteDurationHr}} hours of inactivity.',
                { roomAutoDeleteDurationHr }
            )
        } else {
            return t(
                'Are you sure you want to end this session?\nAll of the collaborators will be removed from the room, and the room will be deleted after you leave.'
            )
        }
    } else {
        if (roomAutoDeleteDurationHr) {
            return t(
                'Are you sure you want to leave this session?\nThe room will be deleted after {{roomAutoDeleteDurationHr}} hours of inactivity.',
                { roomAutoDeleteDurationHr }
            )
        } else {
            return t(
                'Are you sure you want to leave this session?\nThe room will be deleted when everyone leaves the room.'
            )
        }
    }
}
